import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import DataPageLayout from '../components/DataPageLayout';
import {
  cancelQueueBatch,
  getQueueBatch,
  getQueueOverview,
  rescheduleQueueBatch,
} from '../services/queueService';
import '../components/DataPage.css';
import './Dashboard.css';
import { CrossIcon, ScheduleIcon } from '../components/Icons';

function formatCount(value) {
  if (value === null || value === undefined) {
    return '—';
  }
  return Number(value).toLocaleString();
}

function formatDate(value) {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
}

function formatJobState(state) {
  if (state === 'created' || state === 'retry') {
    return 'queued';
  }

  if (state === 'active') {
    return 'processing';
  }

  return state;
}

function DetailField({ label, value, fullWidth = false }) {
  return (
    <div className={`data-page__detail-field${fullWidth ? ' data-page__detail-field--full' : ''}`}>
      <span>{label}</span>
      <p>{value ?? '—'}</p>
    </div>
  );
}

function QueueBatchDetailModal({ batchId, onClose }) {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadBatch() {
      setLoading(true);

      try {
        const data = await getQueueBatch(batchId);
        if (isMounted) {
          setBatch(data);
        }
      } catch (err) {
        if (isMounted) {
          toast.error(err.message);
          setBatch(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadBatch();

    return () => {
      isMounted = false;
    };
  }, [batchId]);

  const jobs = batch?.jobs ?? [];

  return (
    <div className="data-page__modal-backdrop" onClick={onClose}>
      <div
        className="data-page__modal data-page__modal--batch-detail"
        role="dialog"
        aria-modal="true"
        aria-labelledby="queue-batch-detail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="queue-batch-detail-title">Group details</h3>

        <div className="data-page__modal-scroll">
          {loading && <p className="data-page__detail-loading">Loading batch details...</p>}

          {batch && (
            <>
              <div className="data-page__detail-grid">
                <DetailField label="Group ID" value={batch.batchId} fullWidth />
                <DetailField
                  label="Status"
                  value={
                    <span className={`data-page__status data-page__status--${batch.status}`}>
                      {batch.status}
                    </span>
                  }
                />
                <DetailField label="Subject" value={batch.subject} fullWidth />
                <DetailField label="Sender email(s)" value={batch.senderEmail} fullWidth />
                <DetailField label="Total Jobs" value={formatCount(batch.total)} />
                <DetailField label="Queued" value={formatCount(batch.queued)} />
                <DetailField label="Active" value={formatCount(batch.active)} />
                <DetailField label="Completed" value={formatCount(batch.completed)} />
                <DetailField label="Cancelled" value={formatCount(batch.cancelled)} />
                <DetailField label="Failed" value={formatCount(batch.failed)} />
                <DetailField label="Scheduled For" value={formatDate(batch.scheduledFor)} />
                <DetailField label="Created At" value={formatDate(batch.createdAt)} />
              </div>

              <div className="data-page__detail-body">
                <span>Waiting emails ({formatCount(jobs.length)})</span>
                <div className="data-page__batch-detail-table-wrap">
                  <table className="data-page__import-preview-table">
                    <thead>
                      <tr>
                        <th>Recipient</th>
                        <th>Name</th>
                        <th>Sender</th>
                        <th>Status</th>
                        <th>Scheduled For</th>
                        <th>Created At</th>
                        <th>Completed At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.length === 0 ? (
                        <tr>
                          <td colSpan={7}>No emails in this group.</td>
                        </tr>
                      ) : (
                        jobs.map((job) => {
                          const displayState = formatJobState(job.state);

                          return (
                            <tr key={job.id}>
                              <td className="data-page__cell-truncate">{job.recipientEmail ?? '—'}</td>
                              <td className="data-page__cell-truncate">{job.recipientName ?? '—'}</td>
                              <td className="data-page__cell-truncate">{job.senderEmail ?? '—'}</td>
                              <td>
                                <span className={`data-page__status data-page__status--${displayState}`}>
                                  {displayState}
                                </span>
                              </td>
                              <td>{formatDate(job.scheduledFor)}</td>
                              <td>{formatDate(job.createdAt)}</td>
                              <td>{formatDate(job.completedAt)}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="data-page__modal-footer">
          <button type="button" className="data-page__modal-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDialog({ title, message, detail, confirmLabel, loading, onClose, onConfirm }) {
  return (
    <div className="data-page__modal-backdrop" onClick={onClose}>
      <div
        className="data-page__modal data-page__modal--confirm"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <h3>{title}</h3>
        <p className="data-page__confirm-message">{message}</p>
        {detail && <p className="data-page__confirm-detail">{detail}</p>}
        <div className="data-page__modal-actions">
          <button type="button" className="data-page__modal-btn" onClick={onClose} disabled={loading}>
            Close
          </button>
          <button
            type="button"
            className="data-page__modal-btn data-page__modal-btn--danger"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Working...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function RescheduleModal({ batch, loading, onClose, onConfirm }) {
  const [sendAt, setSendAt] = useState('');

  const handleSubmit = (event) => {
    event.preventDefault();
    onConfirm(sendAt);
  };

  return (
    <div className="data-page__modal-backdrop" onClick={onClose}>
      <div
        className="data-page__modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <h3>Reschedule</h3>
        <p className="data-page__confirm-detail">
          Pick a new send time for waiting emails in group {batch.batchId}.
        </p>

        <form onSubmit={handleSubmit}>
          <label className="data-page__modal-field">
            <span>Date &amp; Time</span>
            <input
              type="datetime-local"
              value={sendAt}
              onChange={(event) => setSendAt(event.target.value)}
              required
              disabled={loading}
            />
          </label>

          <div className="data-page__modal-actions">
            <button type="button" className="data-page__modal-btn" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              className="data-page__modal-btn data-page__modal-btn--primary"
              disabled={loading}
            >
              {loading ? 'Rescheduling...' : 'Reschedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ScheduledJobs() {
  const [overview, setOverview] = useState({
    stats: { queued: 0, completed: 0, cancelled: 0, total: 0 },
    batches: [],
  });
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [cancelBatch, setCancelBatch] = useState(null);
  const [rescheduleBatch, setRescheduleBatch] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState(null);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getQueueOverview();
      setOverview(data);
    } catch (err) {
      toast.error(err.message);
      setOverview({ stats: { queued: 0, completed: 0, cancelled: 0, total: 0 }, batches: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const batches = overview.batches ?? [];
  const stats = overview.stats ?? { queued: 0, completed: 0, cancelled: 0, total: 0 };

  const metrics = [
    {
      title: 'Waiting to send',
      value: loading ? '...' : formatCount(stats.queued),
      subtext: 'Emails waiting to send',
      highlight: true,
    },
    {
      title: 'Completed emails',
      value: loading ? '...' : formatCount(stats.completed),
      subtext: 'Emails sent successfully',
    },
    {
      title: 'Cancelled emails',
      value: loading ? '...' : formatCount(stats.cancelled),
      subtext: 'Emails cancelled before sending',
    },
    {
      title: 'Total emails',
      value: loading ? '...' : formatCount(stats.total),
      subtext: 'All emails in the send queue',
      highlight: true,
    },
  ];

  const handleCancelBatch = async () => {
    if (!cancelBatch) {
      return;
    }

    setActionLoadingId(cancelBatch.batchId);
    setSuccessMessage('');

    try {
      const result = await cancelQueueBatch(cancelBatch.batchId);
      setCancelBatch(null);
      setSuccessMessage(`Cancelled ${result.cancelled} waiting email(s).`);
      toast.success(`Cancelled ${result.cancelled} waiting email(s).`);
      await loadOverview();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRescheduleBatch = async (sendAt) => {
    if (!rescheduleBatch) {
      return;
    }

    setActionLoadingId(rescheduleBatch.batchId);
    setSuccessMessage('');

    try {
      const result = await rescheduleQueueBatch(
        rescheduleBatch.batchId,
        new Date(sendAt).toISOString()
      );
      setRescheduleBatch(null);
      setSuccessMessage(
        `Rescheduled ${result.rescheduled} email(s) for ${formatDate(result.sendAt)}.`
      );
      toast.success(`Rescheduled ${result.rescheduled} email(s).`);
      await loadOverview();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoadingId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'batchId',
        label: 'Group ID',
        className: 'data-page__cell-truncate',
      },
      {
        key: 'subject',
        label: 'Subject',
        className: 'data-page__cell-truncate',
      },
      {
        key: 'senderEmail',
        label: 'Sender',
        className: 'data-page__cell-truncate',
      },
      {
        key: 'total',
        label: 'Total',
        render: (row) => formatCount(row.total),
      },
      {
        key: 'queued',
        label: 'Waiting',
        render: (row) => formatCount(row.queued),
      },
      {
        key: 'status',
        label: 'Status',
        render: (row) => (
          <span className={`data-page__status data-page__status--${row.status}`}>
            {row.status}
          </span>
        ),
      },
      {
        key: 'scheduledFor',
        label: 'Send at',
        render: (row) => formatDate(row.scheduledFor),
      },
      {
        key: 'createdAt',
        label: 'Created',
        render: (row) => formatDate(row.createdAt),
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (row) => {
          const canManage = row.queued > 0;
          const isLoading = actionLoadingId === row.batchId;

          return (
            <div className="data-page__actions">
              <button
                type="button"
                className="data-page__action-btn data-page__action-btn--delete"
                onClick={(event) => {
                  event.stopPropagation();
                  setCancelBatch(row);
                }}
                disabled={!canManage || isLoading}
              >
                <CrossIcon />
              </button>
              <button
                type="button"
                className="data-page__action-btn data-page__action-btn--edit"
                onClick={(event) => {
                  event.stopPropagation();
                  setRescheduleBatch(row);
                }}
                disabled={!canManage || isLoading}
              >
                <ScheduleIcon />
              </button>
            </div>
          );
        },
      },
    ],
    [actionLoadingId]
  );

  return (
    <>
      <DataPageLayout
        title="Scheduled Emails"
        subtitle="View and manage emails waiting to send."
        metrics={metrics}
        loading={loading}
        successMessage={successMessage}
        onRefresh={loadOverview}
        columns={columns}
        rows={batches.map((batch) => ({ ...batch, id: batch.batchId }))}
        emptyMessage="No scheduled emails yet."
        totalLabel="batches"
        onRowClick={(row) => setSelectedBatchId(row.batchId)}
      />

      {selectedBatchId && (
        <QueueBatchDetailModal
          batchId={selectedBatchId}
          onClose={() => setSelectedBatchId(null)}
        />
      )}

      {cancelBatch && (
        <ConfirmDialog
          title="Cancel group"
          message="Cancel all waiting emails in this group?"
          detail={`${cancelBatch.queued} waiting email(s) in group ${cancelBatch.batchId}`}
          confirmLabel="Cancel group"
          loading={actionLoadingId === cancelBatch.batchId}
          onClose={() => !actionLoadingId && setCancelBatch(null)}
          onConfirm={handleCancelBatch}
        />
      )}

      {rescheduleBatch && (
        <RescheduleModal
          batch={rescheduleBatch}
          loading={actionLoadingId === rescheduleBatch.batchId}
          onClose={() => !actionLoadingId && setRescheduleBatch(null)}
          onConfirm={handleRescheduleBatch}
        />
      )}
    </>
  );
}

export default ScheduledJobs;
